import React, { useEffect, useState } from 'react';
import { X, Search, Bot, ToggleLeft, ToggleRight, RefreshCw, Loader2 } from 'lucide-react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { aiAssistantService } from '../../services/aiAssistantService';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import PropertySearchChat from './PropertySearchChat';
import AIAssistantChat from './AIAssistantChat';
import { cn } from '../../lib/utils';
import avatarImage from '../../assets/img3d/Avatar.png';

/**
 * ChatbotModal Component
 * Main modal container that switches between search and assistant modes
 */
const ChatbotModal = () => {
  const {
    isOpen,
    closeChatbot,
    mode,
    toggleMode,
    canToggleMode,
    roleConfig,
    userRole,
    clearSession,
    isLoading,
    getCurrentSessionId,
    setCurrentSessionId,
    setAssistantMessages,
  } = useChatbot();

  const [isClearingConversation, setIsClearingConversation] = useState(false);

  // Handle clear conversation (new chat)
  const handleClearConversation = async () => {
    setIsClearingConversation(true);
    try {
      if (mode === 'assistant') {
        const sessionId = getCurrentSessionId();
        if (sessionId) {
          await aiAssistantService.clearSession(sessionId);
        }
        setCurrentSessionId(null);
        setAssistantMessages([{
          role: 'assistant',
          content: roleConfig.welcomeMessage,
          timestamp: new Date(),
        }]);
      } else {
        clearSession();
      }
    } finally {
      // Small delay to ensure smooth transition
      setTimeout(() => {
        setIsClearingConversation(false);
      }, 300);
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        closeChatbot();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeChatbot]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Get header configuration based on mode and role
  const getHeaderConfig = () => {
    if (mode === 'search') {
      return {
        gradient: 'from-orange-200/60 via-orange-100/40 to-white',
        icon: Search,
        title: roleConfig.title,
        subtitle: roleConfig.subtitle,
      };
    }

    // Same pastel orange-white gradient for all roles
    return {
      gradient: 'from-orange-200/60 via-orange-100/40 to-white',
      icon: Bot,
      title: roleConfig.title,
      subtitle: roleConfig.subtitle,
    };
  };

  const headerConfig = getHeaderConfig();
  const HeaderIcon = headerConfig.icon;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 animate-in fade-in duration-200"
        onClick={closeChatbot}
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <Card
          className={cn(
            'w-full max-w-3xl h-[650px] max-h-[90vh]',
            'shadow-2xl flex flex-col overflow-hidden',
            'animate-in fade-in zoom-in-95 duration-200',
            'pointer-events-auto'
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className={cn(
              'px-4 py-4 flex items-center justify-between',
              'bg-gradient-to-r',
              headerConfig.gradient,
              'text-slate-700'
            )}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center bg-orange-100">
                <img 
                  src={avatarImage} 
                  alt="Assistant Avatar" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="font-semibold text-base">{headerConfig.title}</h3>
                <p className="text-xs text-slate-600">{headerConfig.subtitle}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Mode toggle for clients */}
              {canToggleMode && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleMode}
                  className="h-9 px-3 text-slate-600 hover:text-slate-800 hover:bg-slate-100"
                  title={mode === 'search' ? 'Passer en mode Assistant' : 'Passer en mode Recherche'}
                >
                  {mode === 'search' ? (
                    <>
                      <Bot className="h-4 w-4 mr-1.5" />
                      <span className="text-xs">Assistant</span>
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-1.5" />
                      <span className="text-xs">Recherche</span>
                    </>
                  )}
                </Button>
              )}

              {/* Nouveau chat button - only in assistant mode */}
              {mode === 'assistant' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearConversation}
                  disabled={isLoading || isClearingConversation}
                  className={cn(
                    'h-9 px-3 text-slate-600 hover:text-slate-800 hover:bg-slate-100',
                    isClearingConversation && 'opacity-70 cursor-wait'
                  )}
                  title="Nouveau chat"
                >
                  {isClearingConversation ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                      <span className="text-xs">Réinitialisation...</span>
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-1.5" />
                      <span className="text-xs">Nouveau chat</span>
                    </>
                  )}
                </Button>
              )}

              {/* Close button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={closeChatbot}
                className="h-9 w-9 text-slate-600 hover:text-slate-800 hover:bg-slate-100"
                aria-label="Fermer"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col min-h-0 bg-white">
            {mode === 'search' ? (
              <PropertySearchChat />
            ) : (
              <AIAssistantChat />
            )}
          </div>
        </Card>
      </div>
    </>
  );
};

export default ChatbotModal;

